import type { Contract } from 'common/contract'
import type { MarketDraft } from 'common/drafts'
import { canInheritLinkedCloseTime } from 'common/market-links'
import dayjs from 'dayjs'
import type { FormState } from '../contextual-editor-panel'

export function getCreateMarketCloseParams(
  state: Pick<
    FormState,
    | 'outcomeType'
    | 'neverCloses'
    | 'closeDate'
    | 'closeHoursMinutes'
    | 'linkedToContractId'
    | 'hasManuallyEditedCloseDate'
  >
): { closeTime?: number; neverCloses?: true } {
  if (isNeverClosingPoll(state)) return { neverCloses: true }
  // The displayed parent date is a preview. Let the server copy the current
  // parent deadline under lock unless the creator explicitly chose a date.
  if (state.linkedToContractId && !hasManualCloseTime(state)) return {}
  if (!state.closeDate) return {}
  const closeTime = new Date(
    state.closeDate + 'T' + (state.closeHoursMinutes || '23:59')
  ).getTime()
  if (!Number.isFinite(closeTime))
    throw new Error('Please select a valid close date and time.')
  return { closeTime }
}

export function getCloseTimeFields(closeTime: number | undefined) {
  // Duplicated markets use zero to mean no reusable deadline.
  if (closeTime === undefined || !Number.isFinite(closeTime) || closeTime <= 0)
    return undefined
  const date = dayjs(closeTime)
  if (!date.isValid()) return undefined
  return {
    closeDate: date.format('YYYY-MM-DD'),
    closeHoursMinutes: date.format('HH:mm'),
    neverCloses: false,
  }
}

export function hasManualCloseTime(
  state: Pick<
    FormState,
    'hasManuallyEditedCloseDate' | 'closeDate' | 'neverCloses'
  >
) {
  // Old local drafts have no flag. Preserve their existing date conservatively.
  return (
    state.hasManuallyEditedCloseDate ?? (!!state.closeDate || state.neverCloses)
  )
}

/**
 * Never, or a date the creator cleared. The close-date picker shows Never for
 * any poll without a date, so a cleared date must not fall back to the linked
 * market's deadline.
 */
export function isNeverClosingPoll(
  state: Pick<
    FormState,
    'outcomeType' | 'neverCloses' | 'closeDate' | 'hasManuallyEditedCloseDate'
  >
) {
  return (
    state.outcomeType === 'POLL' &&
    (state.neverCloses || (hasManualCloseTime(state) && !state.closeDate))
  )
}

export function selectLinkedMarket(
  state: FormState,
  link:
    | { contractId: string; relation: NonNullable<FormState['linkRelation']> }
    | undefined
): FormState {
  const clearDefault =
    state.linkedToContractId !== link?.contractId && !hasManualCloseTime(state)
  return {
    ...state,
    linkedToContractId: link?.contractId,
    linkRelation: link?.relation,
    ...(clearDefault
      ? {
          closeDate: undefined,
          closeHoursMinutes: undefined,
          neverCloses: false,
          inheritedCloseTimeFrom: undefined,
        }
      : {}),
  }
}

export function isLinkedCloseTimePending(state: FormState) {
  return (
    !!state.linkedToContractId &&
    canInheritLinkedCloseTime(state.outcomeType) &&
    !hasManualCloseTime(state) &&
    state.inheritedCloseTimeFrom !== state.linkedToContractId
  )
}

/** Whether the loaded parent should supply the form's inherited deadline. */
export function shouldApplyLinkedMarketCloseTime(
  state: FormState,
  parent: Pick<Contract, 'id' | 'closeTime'>
) {
  if (
    state.linkedToContractId !== parent.id ||
    hasManualCloseTime(state) ||
    !canInheritLinkedCloseTime(state.outcomeType) ||
    (state.outcomeType === 'POLL' && state.neverCloses)
  )
    return false
  const fields = getCloseTimeFields(parent.closeTime)
  // Provenance can survive a restore while the parent's deadline has moved.
  // Compare the displayed fields too; once current, no further update is needed.
  return (
    state.inheritedCloseTimeFrom !== parent.id ||
    state.closeDate !== fields?.closeDate ||
    state.closeHoursMinutes !== fields?.closeHoursMinutes ||
    state.neverCloses
  )
}

export function getLinkedCloseTimeDescription(state: FormState) {
  if (isNeverClosingPoll(state)) return 'Never'
  if (!state.linkedToContractId || hasManualCloseTime(state)) return undefined
  if (!canInheritLinkedCloseTime(state.outcomeType)) return 'Never'
  if (isLinkedCloseTimePending(state))
    return 'Uses linked market deadline (loading…)'
  if (!state.closeDate)
    return state.outcomeType === 'POLL' ? 'Never' : 'Choose a close date'
  return undefined
}

export function applyLinkedMarketCloseTime(
  state: FormState,
  parent: Pick<Contract, 'id' | 'closeTime'>
): FormState {
  if (!shouldApplyLinkedMarketCloseTime(state, parent)) return state
  const closeTimeFields = getCloseTimeFields(parent.closeTime)
  return {
    ...state,
    closeDate: undefined,
    closeHoursMinutes: undefined,
    neverCloses: false,
    ...closeTimeFields,
    inheritedCloseTimeFrom: parent.id,
  }
}

export function applySuggestedCloseTime(
  state: FormState,
  question: string,
  closeTime: number
): FormState {
  const closeTimeFields = getCloseTimeFields(closeTime)
  if (
    state.question !== question ||
    state.outcomeType === 'POLL' ||
    !canInheritLinkedCloseTime(state.outcomeType) ||
    state.linkedToContractId ||
    hasManualCloseTime(state) ||
    !closeTimeFields
  )
    return state
  return { ...state, ...closeTimeFields }
}

export function restoreMarketDraft(
  state: FormState,
  data: MarketDraft['data']
): FormState {
  return {
    ...state,
    question: data.question,
    description: data.description,
    outcomeType: data.outcomeType,
    answers: data.answers,
    answerProbs: undefined,
    closeDate: data.closeDate,
    closeHoursMinutes: data.closeHoursMinutes,
    inheritedCloseTimeFrom: data.inheritedCloseTimeFrom,
    neverCloses: data.neverCloses ?? false,
    hasManuallyEditedCloseDate:
      data.hasManuallyEditedCloseDate ??
      (!!data.closeDate || !!data.neverCloses),
    // Assign even when absent: an older/unlinked draft must clear a current link.
    linkedToContractId: data.linkedToContractId,
    linkRelation: data.linkRelation,
    visibility: data.visibility,
    selectedGroups: data.selectedGroups,
  }
}
